import { SurveyUserRoles } from '@constants/user';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';
import { PermissionsSurvey } from './types';
import { SurveyPermission, SurveyPermissionType } from '../../types/permission';

/**
 * This class is copied from the one in API and adjusted with pre-fetching survey workgroups
 */
export class WorkgroupPermissions {
  public static checkHasSurveyUserRoles({
    survey,
    userId,
    roles,
  }: {
    survey: Pick<PermissionsSurvey, 'workgroups'>;
    userId: string;
    roles: SurveyUserRoles[];
  }) {
    return survey.workgroups?.some((workgroup) => {
      return (
        workgroup.users.some((user) => user._id === userId) &&
        (this.checkIsSurveyWorkgroupPermission({
          permission: workgroup.permission,
          access: DataScopeAccess.Full,
          roles,
        }) ||
          // Partial access is considered for Stakeholder and Verifier roles only, so we count 'Partial' access Admin role if included. Ignore None access for now.
          this.checkIsSurveyWorkgroupPermission({
            permission: workgroup.permission,
            access: DataScopeAccess.Partial,
            roles: roles.filter((r) => r === SurveyUserRoles.Admin),
          }))
      );
    });
  }

  public static checkHasUtrvsUserRoles({
    utrvs,
    userId,
    roles,
    survey,
  }: {
    utrvs: { _id: string }[];
    userId: string;
    roles: SurveyUserRoles[];
    survey: Pick<PermissionsSurvey, 'workgroups'>;
  }): boolean {
    if (!utrvs.length) {
      return false;
    }

    const surveyWorkgroups = survey.workgroups?.filter(({ permission }) =>
      this.checkIsSurveyWorkgroupPermission({ permission, access: DataScopeAccess.Partial, roles }),
    );
    if (!surveyWorkgroups?.length) {
      return false;
    }

    const allowedUtrvIds = new Set<string>();
    for (const surveyWorkgroup of surveyWorkgroups) {
      if (surveyWorkgroup.users.some((user) => user._id === userId)) {
        surveyWorkgroup.utrvIds.forEach((utrvId) => allowedUtrvIds.add(utrvId));
      }
    }

    return utrvs.every((utrv) => allowedUtrvIds.has(utrv._id));
  }

  private static checkIsSurveyWorkgroupPermission({
    permission,
    access,
    roles,
  }: {
    permission: SurveyPermission;
    access: DataScopeAccess;
    roles: SurveyUserRoles[];
  }) {
    return (
      permission.type === SurveyPermissionType.Workgroup &&
      permission.access === access &&
      permission.roles.some((role) => roles.includes(role))
    );
  }
}
